import { useState } from 'react'

import type {
  AccountTransactionsImportMutation,
  AccountTransactionsImportValidateMutation,
} from 'generated/resources/graphql'
import {
  AccountPageDocument,
  AccountTransactionsImportDocument,
  AccountTransactionsImportValidateDocument,
} from 'generated/resources/graphql'

type ImportBankingTransactionImportResultPayload =
  AccountTransactionsImportMutation['bankingMutations']['importBankingTransactionImportResult']

type ValidateBankingTransactionImportResultPayload =
  AccountTransactionsImportValidateMutation['bankingMutations']['validateBankingTransactionImportResult']

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'

export const useUploadImportFile = () => {
  const [uploadImportFile, { loading, data }] = useMutation(AccountTransactionsImportDocument, {
    extractorFn: (data) => data.bankingMutations.importBankingTransactionImportResult,
    refetchQueries: [AccountPageDocument],
  })
  const [uploadImportFileCheck, { loading: loadingCheck, data: check, error }] = useMutation(
    AccountTransactionsImportValidateDocument,
    {
      extractorFn: (data) => data.bankingMutations.validateBankingTransactionImportResult,
    }
  )

  const [errorChecks, setErrorChecks] = useState<string[] | null>(null)
  const [errors, setErrors] = useState<string[] | null>(null)
  const [rowsProcessed, setRowsProcessed] = useState<number | null>(null)
  const [rowsPersisted, setRowsPersisted] = useState<number | null>(null)

  const getErrorsInStringArray = (
    errorsParam:
      | ValidateBankingTransactionImportResultPayload['errors']
      | ImportBankingTransactionImportResultPayload['errors']
  ) => {
    return errorsParam
      .map((el) => {
        if (el.__typename === 'SimpleError') {
          return el.__typename
        } else if (el.__typename === 'StructuredError') {
          return el.messages
        } else {
          return JSON.stringify(el)
        }
      })
      .flat()
  }

  useUnsafeEffect(() => {
    if (check) {
      const checkErrors = check.bankingMutations.validateBankingTransactionImportResult.errors
      if (checkErrors.length > 0) {
        setErrorChecks(getErrorsInStringArray(checkErrors))
      } else {
        setErrorChecks(null)
        const result =
          check.bankingMutations.validateBankingTransactionImportResult.transactionImportResult
        setRowsProcessed(result ? result.added : 0)
      }
    }
  }, [check])

  useUnsafeEffect(() => {
    if (data) {
      const dataErrors = data.bankingMutations.importBankingTransactionImportResult.errors
      if (dataErrors.length > 0) {
        setErrors(getErrorsInStringArray(dataErrors))
      } else {
        setErrors(null)
        const result =
          data.bankingMutations.importBankingTransactionImportResult.transactionImportResult
        setRowsPersisted(result ? result.added : 0)
      }
    }
  }, [data])

  return {
    loading,
    loadingCheck,
    rowsProcessed,
    rowsPersisted,
    errorChecks,
    errors,
    uploadImportFileToCheck(bankAccountId: number, file: File) {
      return uploadImportFileCheck({
        variables: {
          bankAccountId,
          file,
        },
      })
    },
    uploadImportFile(bankAccountId: number, file: File) {
      return uploadImportFile({
        variables: {
          bankAccountId,
          file,
        },
      })
    },
  }
}
